/**
 * Account settings and session management.
 *
 * The session list shows only safe metadata -- an identifier, timestamps, a summarised user agent,
 * and an address. It contains no session token and no stored digest, because the Platform API does
 * not return them; there is nothing here to accidentally render.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import {
  useAuthProviders,
  useDeleteAccount,
  useDisconnectIdentity,
  useForgotPassword,
  useLogoutAll,
  useRevokeSession,
  useSession,
  useSessions,
  useSignInMethods,
  useUpdateProfile,
  useWorkspace,
} from '../../lib/api';
import { GoogleMark, oauthErrorMessage, oauthStartHref } from '../../components/oauth-buttons';
import { Button, Card, Field, FormError, TextInput, buttonClasses } from '../../components/ui';

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function SettingsRoute() {
  const { data: session } = useSession();
  const { data, isLoading } = useSessions();
  const revokeSession = useRevokeSession();
  const logoutAll = useLogoutAll();
  const navigate = useNavigate();
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);

  const sessions = data?.sessions ?? [];

  const handleLogoutAll = () => {
    logoutAll.mutate(undefined, {
      // Logout-all includes this session, so the only correct destination is the login screen.
      onSettled: () => void navigate({ to: '/login' }),
    });
  };

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="mt-1 text-sm text-neutral-600">Your account and signed-in devices.</p>
      </div>

      <Card>
        <h2 className="text-base font-semibold">Profile</h2>
        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Email</dt>
            <dd>{session?.user.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Email verified</dt>
            <dd>{session?.user.emailVerified === true ? 'Yes' : 'Not yet'}</dd>
          </div>
        </dl>
        {session?.user !== undefined && <ProfileNameForm name={session.user.displayName} />}
      </Card>

      <SignInMethodsCard email={session?.user.email} />

      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 className="text-base font-semibold">Active sessions</h2>
          <Button variant="danger" onClick={handleLogoutAll} disabled={logoutAll.isPending}>
            {logoutAll.isPending ? 'Signing out...' : 'Sign out everywhere'}
          </Button>
        </div>

        {isLoading ? (
          <p className="px-5 py-6 text-sm text-neutral-500">Loading...</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr className="border-b border-neutral-200">
                <th scope="col" className="px-5 py-2 font-medium">
                  Device
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Address
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Last seen
                </th>
                <th scope="col" className="px-5 py-2" />
              </tr>
            </thead>
            <tbody>
              {sessions.map((item) => (
                <tr key={item.id} className="border-b border-neutral-100 last:border-0">
                  <td className="px-5 py-3">
                    <span className="block max-w-xs truncate" title={item.userAgent ?? undefined}>
                      {item.userAgent ?? 'Unknown device'}
                    </span>
                    {item.current && (
                      <span className="text-xs font-medium text-green-700">This device</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-neutral-600">
                    {item.ipAddress ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-neutral-600">{formatDate(item.lastSeenAt)}</td>
                  <td className="px-5 py-3 text-right">
                    {!item.current && (
                      <Button
                        variant="secondary"
                        onClick={() => revokeSession.mutate({ id: item.id })}
                        disabled={revokeSession.isPending}
                        aria-label="Revoke this session"
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="border-t border-neutral-200 px-5 py-3 text-xs text-neutral-500">
          Revoking a session takes effect immediately on its next request.
        </p>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-red-800">Close account</h2>
        <p className="mt-2 max-w-2xl text-sm text-neutral-600">
          This deletes your API keys, usage history and request logs, and signs you out everywhere.
          It happens immediately and cannot be undone.
        </p>
        <div className="mt-4">
          <Button variant="danger" onClick={() => setConfirmingDeletion(true)}>
            Close account
          </Button>
        </div>
      </Card>

      {confirmingDeletion && <CloseAccountDialog onCancel={() => setConfirmingDeletion(false)} />}
    </>
  );
}

/**
 * How this account can be signed in to: a password, Google, or both.
 *
 * An account made with Google has no password, and gets one the way a forgotten one is replaced --
 * a link to its address. Disconnecting Google is refused while it is the only way in; the API
 * enforces that too, and the button says so first rather than failing.
 */
function SignInMethodsCard({ email }: { email: string | undefined }) {
  const { data: providers } = useAuthProviders();
  const { data: methods } = useSignInMethods();
  const disconnect = useDisconnectIdentity();
  const sendPasswordLink = useForgotPassword();
  // Connecting Google comes back here, with the reason when it did not work.
  const search: { oauth_error?: string } = useSearch({ strict: false });
  const [error, setError] = useState<string | null>(() => oauthErrorMessage(search.oauth_error));

  if (methods === undefined) return null;

  const google = methods.identities.find((identity) => identity.provider === 'google');
  const googleOffered = providers?.includes('google') === true;
  const onlyWayIn = !methods.hasPassword && methods.identities.length === 1;

  const handleDisconnect = (id: string) => {
    setError(null);
    disconnect.mutate(
      { id },
      {
        onError: (failure) => {
          setError(
            failure instanceof ApiError ? failure.message : 'Google could not be disconnected.',
          );
        },
      },
    );
  };

  const handleSendPasswordLink = () => {
    if (email === undefined) return;
    setError(null);
    sendPasswordLink.mutate(
      { email },
      {
        onError: () => setError('The link could not be sent. Please try again.'),
      },
    );
  };

  return (
    <Card data-testid="sign-in-methods">
      <h2 className="text-base font-semibold">Sign-in methods</h2>
      <div className="mt-3">
        <FormError message={error} />
      </div>
      <ul className="mt-1 divide-y divide-neutral-100 text-sm">
        <li className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div>
            <p className="font-medium">Password</p>
            <p className="text-neutral-600">
              {methods.hasPassword ? 'Set.' : 'Not set. You sign in with Google.'}
            </p>
          </div>
          {!methods.hasPassword &&
            (sendPasswordLink.isSuccess ? (
              <span role="status" className="text-sm text-green-700">
                Check your email for a link to set a password.
              </span>
            ) : (
              <Button
                variant="secondary"
                onClick={handleSendPasswordLink}
                disabled={email === undefined || sendPasswordLink.isPending}
              >
                {sendPasswordLink.isPending ? 'Sending...' : 'Email me a link to set one'}
              </Button>
            ))}
        </li>

        {(google !== undefined || googleOffered) && (
          <li className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-3">
              <GoogleMark />
              <div>
                <p className="font-medium">Google</p>
                <p className="text-neutral-600">
                  {google === undefined ? 'Not connected.' : `Connected as ${google.email}.`}
                </p>
              </div>
            </div>
            {google !== undefined ? (
              <Button
                variant="secondary"
                onClick={() => handleDisconnect(google.id)}
                disabled={onlyWayIn || disconnect.isPending}
                aria-describedby={onlyWayIn ? 'google-only-way-in' : undefined}
              >
                {disconnect.isPending ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : (
              <a
                href={oauthStartHref('google', { intent: 'link' })}
                className={buttonClasses('secondary')}
              >
                Connect Google
              </a>
            )}
          </li>
        )}
      </ul>
      {onlyWayIn && google !== undefined && (
        <p id="google-only-way-in" className="mt-2 text-xs text-neutral-500">
          Google is the only way to sign in to this account. Set a password before disconnecting it.
        </p>
      )}
    </Card>
  );
}

const profileSchema = z.object({
  displayName: z
    .string()
    .trim()
    .max(100, 'Use at most 100 characters.')
    // eslint-disable-next-line no-control-regex -- the API refuses these, so the form says so first.
    .refine((value) => !/[\u0000-\u001f\u007f]/.test(value), {
      message: 'Use letters, numbers and punctuation only.',
    }),
});

type ProfileForm = z.infer<typeof profileSchema>;

/**
 * The name the account is greeted by.
 *
 * Optional at registration and editable here, so a customer who skipped it or made a typo is not
 * stuck with the result. Clearing the field removes the name.
 */
function ProfileNameForm({ name }: { name: string | null }) {
  const updateProfile = useUpdateProfile();
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { displayName: name ?? '' },
  });

  // Follows the stored name when it changes -- after a save, or a change made in another tab --
  // without resetting the field on every unrelated re-render while someone is typing.
  useEffect(() => {
    reset({ displayName: name ?? '' });
  }, [name, reset]);

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    setSaved(false);
    updateProfile.mutate(
      { displayName: values.displayName === '' ? null : values.displayName },
      {
        onSuccess: () => setSaved(true),
        onError: (error) => {
          setFormError(error instanceof ApiError ? error.message : 'Could not save your name.');
        },
      },
    );
  });

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      className="mt-4 flex flex-col gap-4"
      noValidate
      data-testid="profile-name-form"
    >
      <FormError message={formError} />
      <Field
        label="Name"
        htmlFor="profile-name"
        hint="Used to greet you here and in emails from us. Leave it empty to remove it."
        error={errors.displayName?.message}
      >
        <TextInput
          id="profile-name"
          autoComplete="name"
          invalid={errors.displayName !== undefined}
          {...register('displayName')}
        />
      </Field>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={updateProfile.isPending}>
          {updateProfile.isPending ? 'Saving...' : 'Save name'}
        </Button>
        {saved && (
          <span role="status" className="text-sm text-green-700">
            Saved
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Confirmation for the one action in the product that cannot be undone.
 *
 * The workspace name has to be typed. Every other destructive action here fires on a single click,
 * which is right for revoking a key -- that is recoverable by making another one -- and wrong for
 * this. Typing the name is what makes a mistaken click impossible rather than merely discouraged.
 *
 * Built on the dialog shape already used by the one-time key dialog on the API keys page: a fixed
 * overlay, `aria-modal`, Escape to dismiss, and focus moved on open. There is no shared dialog
 * primitive in this application yet; when a third one appears it is worth extracting.
 */
function CloseAccountDialog({ onCancel }: { onCancel: () => void }) {
  const { data: workspace } = useWorkspace();
  const deleteAccount = useDeleteAccount();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const expected = workspace?.name ?? '';

  const schema = z.object({
    confirmation: z
      .string()
      // Compared after trimming so a trailing space from a paste is not treated as a mistake.
      .refine((value) => value.trim() === expected && expected !== '', {
        message: 'Type the workspace name exactly.',
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });

  useEffect(() => {
    // Focus starts on Cancel, not on the field: the safe action should be the one a stray Enter
    // reaches first.
    cancelButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const onSubmit = handleSubmit(() => {
    setFormError(null);
    deleteAccount.mutate(undefined, {
      // The session is gone either way, so the destination is the login screen on success. On
      // failure the account still exists and the message has to be shown.
      onSuccess: () => void navigate({ to: '/login' }),
      onError: (error) => {
        setFormError(
          error instanceof ApiError ? error.message : 'The account could not be closed.',
        );
      },
    });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-account-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
      >
        <h2 id="close-account-title" className="text-base font-semibold text-red-800">
          Close this account?
        </h2>
        <p className="mt-2 text-sm text-neutral-700">
          Your API keys stop working immediately and your usage history is deleted. This cannot be
          undone.
        </p>

        <form onSubmit={(event) => void onSubmit(event)} className="mt-4" noValidate>
          <FormError message={formError} />
          <Field
            label={`Type ${expected} to confirm`}
            htmlFor="close-account-confirmation"
            error={errors.confirmation?.message}
          >
            <TextInput
              id="close-account-confirmation"
              autoComplete="off"
              invalid={errors.confirmation !== undefined}
              {...register('confirmation')}
            />
          </Field>

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="secondary" ref={cancelButtonRef} onClick={onCancel}>
              Cancel
            </Button>
            {/*
              Named differently from the button that opened this dialog. Two controls reading
              "Close account" are indistinguishable to a screen reader, and one of them merely opens
              a dialog while the other destroys the account.
            */}
            <Button type="submit" variant="danger" disabled={deleteAccount.isPending}>
              {deleteAccount.isPending ? 'Closing...' : 'Close account permanently'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
